import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, {Dayjs} from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {isEmpty} from "lodash";
import React from "react";
import {CardDetails} from "../../../../enums";
import {Container, Text} from "../../../../ui";
import {CustomDialog, IconAlertOutlined, NotificationCard} from "../../../bounce-ui";
import {FormattedDate} from "../../../components";
import {PaymentDetails, PaymentType} from "../../../declarations";
import {datePickerStyle} from "../../../helpers";
import {PaymentContext, TermsType} from "../../payment";
import {IHighlightedInstallment} from "../utils";
import {PayNextCard} from "./PayNextCard";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface IPayNextContent {
  buttonTouched: boolean;
  setButtonTouched: (touched: boolean) => void;
  isLoading: boolean;
  numberOfInstallments: number;
  highlightedInstallments: IHighlightedInstallment[];
  paymentDate: Dayjs;
  setPaymentDate: (date: Dayjs) => void;
  today: Dayjs;
  maxPaymentDate: Dayjs | null;
  isMobile: boolean;
  showThreeDaysWarning: boolean | undefined;
  nextPayment: IHighlightedInstallment | undefined;
  accountId: string;
  currentPaymentMethod: string | undefined;
  allPaymentMethods?: CardDetails[];
  buttonText: string;
  closeHandler: () => void;
  nextPaymentDate: Dayjs;
  activePaymentPlanId: string;
  nonAutopay: boolean;
  hasSpringPermission?: boolean;
  selectedCardId: number | string | undefined;
  setSelectedCardId: (id: number | string | undefined) => void;
  paymentHandler: (
    paymentType: PaymentType,
    paymentDetails: PaymentDetails,
    isChangePaymentMethod: boolean
  ) => Promise<void>;
  updatePaymentHandler: (
    paymentType: PaymentType,
    paymentDetails: PaymentDetails,
    isChangePaymentMethod: boolean
  ) => Promise<void>;
  PaymentMethodWithWalletComponent: React.FC<any>;
  page: string;
}

const PayNextContent = ({
  buttonTouched,
  setButtonTouched,
  isLoading,
  numberOfInstallments,
  highlightedInstallments,
  paymentDate,
  setPaymentDate,
  today,
  maxPaymentDate,
  isMobile,
  showThreeDaysWarning,
  nextPayment,
  accountId,
  currentPaymentMethod,
  allPaymentMethods,
  buttonText,
  closeHandler,
  nextPaymentDate,
  activePaymentPlanId,
  nonAutopay,
  hasSpringPermission,
  selectedCardId,
  setSelectedCardId,
  paymentHandler,
  updatePaymentHandler,
  PaymentMethodWithWalletComponent,
  page,
}: IPayNextContent) => {
  const onlyPayNow = maxPaymentDate?.isBefore(today, "day") || maxPaymentDate?.isSame(today, "day");
  const getMaxPaymentDate = () => {
    if (onlyPayNow) return today;
    return maxPaymentDate || undefined;
  };
  return (
    <PaymentContext.Provider value={{buttonTouched, setButtonTouched, isLoading}}>
      <CustomDialog title={"Update payment details"}>
        <Container fullWidth sx={{gap: "20px"}}>
          <Container fullWidth sx={{alignItems: "start"}}>
            {!isEmpty(highlightedInstallments) && (
              <PayNextCard installment={highlightedInstallments[0]} numberOfInstallments={numberOfInstallments} />
            )}
          </Container>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Payment date"
              sx={{width: "100%"}}
              value={onlyPayNow ? today : paymentDate}
              minDate={today}
              maxDate={getMaxPaymentDate()}
              onChange={(event: any) => {
                setPaymentDate(event);
              }}
              slotProps={datePickerStyle(isMobile)}
            />
          </LocalizationProvider>
          {showThreeDaysWarning && nextPayment && (
            <NotificationCard color={"yellow"} sx={{width: "100%"}}>
              <Container fullWidth row>
                <IconAlertOutlined style={{width: "40px", height: "40px"}} />
                <Text size={14} variant={"inter400"}>
                  You chose a date that is close to your next payment on <FormattedDate date={nextPayment.due_date} />
                </Text>
              </Container>
            </NotificationCard>
          )}
          <PaymentMethodWithWalletComponent
            accountId={accountId}
            paymentHandler={onlyPayNow || paymentDate.isSame(today, "day") ? paymentHandler : updatePaymentHandler}
            currentMethodType={currentPaymentMethod || ""}
            allPaymentMethods={allPaymentMethods}
            page={page}
            submitTitle={onlyPayNow || paymentDate.isSame(today, "day") ? "Pay now" : buttonText}
            termsAndConditionsId={TermsType.SHORT_PAYMENT}
            closeHandler={closeHandler}
            disable={!onlyPayNow && (paymentDate.isBefore(today, "day") || paymentDate.isAfter(nextPaymentDate, "day"))}
            paymentPlanId={activePaymentPlanId}
            isMethodNextInstallmentOnly={nonAutopay && hasSpringPermission}
            highlightedInstallment={highlightedInstallments[0]}
            paymentDate={paymentDate}
            selectedCardId={selectedCardId}
            setSelectedCardId={setSelectedCardId}
          />
        </Container>
      </CustomDialog>
    </PaymentContext.Provider>
  );
};

export {PayNextContent};
